/**
 * StepperComponent - A reusable Web Component for multi-step navigation
 *
 * A custom HTML element that manages multi-screen stepper/wizard workflows.
 * Uses Shadow DOM for style encapsulation and slots for content projection.
 *
 * @class StepperComponent
 * @extends HTMLElement
 *
 * @example
 * // HTML Usage
 * <stepper-component>
 *   <div class="screen">Screen 1</div>
 *   <div class="screen">Screen 2</div>
 *   <div class="screen">Screen 3</div>
 * </stepper-component>
 *
 * @example
 * // JavaScript Navigation
 * const stepper = document.querySelector('stepper-component');
 * stepper.dispatchEvent(new CustomEvent('stepper:next'));
 * stepper.dispatchEvent(new CustomEvent('stepper:prev'));
 * stepper.dispatchEvent(new CustomEvent('stepper:goto', { detail: { index: 2 } }));
 *
 * @example
 * // CSS Customization
 * stepper-component {
 *   --stepper-transition-override: 0.6s ease-in-out;
 *   --stepper-scale-override: 0.9;
 *   --stepper-opacity-hidden-override: 0.2;
 * }
 *
 * @fires stepper:changed - Dispatched when active screen changes
 * @fires stepper:next - Listen for next screen navigation
 * @fires stepper:prev - Listen for previous screen navigation
 * @fires stepper:goto - Listen for goto screen navigation
 */
class StepperComponent extends HTMLElement {
  /**
   * Root stepper container element (shadow DOM)
   * @type {HTMLElement|null}
   * @private
   */
  stepper = null;

  /**
   * Screen container element that holds the slot (shadow DOM)
   * @type {HTMLElement|null}
   * @private
   */
  screenContainer = null;

  /**
   * Current active screen index
   * @type {number}
   * @private
   */
  currentIndex = 0;

  /**
   * Constructor - Initializes the component
   *
   * @constructor
   */
  constructor() {
    super();
  }

  /**
   * Lifecycle hook - Called when element is inserted into the DOM
   * Initializes shadow DOM, injects styles, creates container structure,
   * and sets up event listeners
   *
   * @returns {void}
   */
  connectedCallback() {
    this.attachShadow({ mode: 'open' });

    this.injectStyles();

    // Create the container structure
    this.createContainerElement();

    // Initialize screen indices
    this.initScreenIndices();

    // Setup event listeners
    this.setupEventListeners();
  }

  /**
   * Initializes all light DOM children with screen indices and active state
   * Sets the first screen as active by default
   *
   * Iterates through light DOM children and:
   * - Adds data-screen-index attribute
   * - Sets data-active="true" for first screen
   * - Sets data-active="false" for other screens
   *
   * @returns {void}
   */
  initScreenIndices() {
    let index = 0;
    for (const child of this.children) {
      child.setAttribute('data-screen-index', index);
      // Hide all screens except first
      if (index === 0) {
        child.setAttribute('data-active', 'true');
      } else {
        child.setAttribute('data-active', 'false');
        child.setAttribute('inert', '');
      }
      index += 1;
    }
  }

  /**
   * Injects encapsulated styles into the shadow DOM
   * Defines animations, transitions, and layout for the stepper component
   *
   * Styles include:
   * - Container layout and overflow handling
   * - Screen container flex layout
   * - Slot transitions and transforms
   * - Slotted element opacity and scale animations
   * - CSS custom properties for customization
   *
   * @returns {void}
   * @private
   */
  injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
    :host {
      display: block;
      height: var(--stepper-height-override, auto);
      --stepper-gap: var(--stepper-gap-override, 0);
      --stepper-bg: var(--stepper-bg-override, transparent);
      --stepper-transition: var(--stepper-transition-override, 0s cubic-bezier(0.4, 0, 0.2, 1));
      --stepper-screen-padding: var(--stepper-screen-padding-override, 0);
      --stepper-scale: var(--stepper-scale-override, 0.95);
      --stepper-opacity-hidden: var(--stepper-opacity-hidden-override, 0);
      --stepper-opacity-visible: var(--stepper-opacity-visible-override, 1);
      --transform: 0;
    }
    
    [data-stepper-container] {
      width: 100%;
      height: 100%;
      position: relative;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
    }
    
    [data-stepper-screen-container] {
      display: flex;
      flex-direction: row;
      width: 100%;
      height: 100%;
      position: relative;
      flex: 1;
    }

    [data-stepper-screen-container] slot {
      transition: 
        transform var(--stepper-transition),
        opacity var(--stepper-transition);
      display: flex;
      flex-direction: row;
      width: 100%;
      height: 100%;
      flex: 1;
    }

    [data-stepper-container] [data-stepper-screen-container] slot {
      transform: translateX(var(--transform));
    }

    /* Hidden screens - fade out and scale slightly */
    ::slotted([data-active="false"]) {
      opacity: var(--stepper-opacity-hidden);
      transform: scale(var(--stepper-scale));
      pointer-events: none;
      transition: 
        opacity var(--stepper-transition),
        transform var(--stepper-transition);
    }

    /* Active screen - full opacity and normal scale */
    ::slotted([data-active="true"]) {
      opacity: var(--stepper-opacity-visible);
      transform: scale(1);
      pointer-events: auto;
      transition: 
        opacity var(--stepper-transition),
        transform var(--stepper-transition);
    }

    ::slotted(*) {
      width: 100%;
      height: 100%;
      flex: 0 0 100%;
    }
  `;

    this.shadowRoot.appendChild(style);
  }

  /**
   * Creates the shadow DOM structure for the stepper component
   *
   * Builds hierarchy:
   * - data-stepper-container (root)
   *   └── data-stepper-screen-container
   *       └── <slot></slot> (projects light DOM children)
   *
   * @returns {void}
   * @private
   */
  createContainerElement() {
    // Root container
    this.stepper = document.createElement('div');
    this.stepper.setAttribute('data-stepper-container', '');

    // Screen container with slot - ONLY append to shadowRoot
    this.screenContainer = document.createElement('div');
    this.screenContainer.setAttribute('data-stepper-screen-container', '');
    this.screenContainer.innerHTML = '<slot></slot>';

    // Build shadow DOM hierarchy
    this.stepper.appendChild(this.screenContainer);

    // ONLY append to shadowRoot, NOT to light DOM
    this.shadowRoot.appendChild(this.stepper);
  }

  /**
   * Sets up event listeners for stepper navigation
   * Listens for custom events and triggers appropriate navigation methods
   *
   * Events handled:
   * - stepper:next - Navigate to next screen
   * - stepper:prev - Navigate to previous screen
   * - stepper:goto - Navigate to specific screen (requires detail.index)
   *
   * @returns {void}
   * @private
   */
  setupEventListeners() {
    // Listen for navigation events
    this.addEventListener('stepper:next', () => this.nextScreen());
    this.addEventListener('stepper:prev', () => this.prevScreen());
    this.addEventListener('stepper:goto', (e) =>
      this.goToScreen(e.detail.index),
    );
    this.addEventListener('stepper:skip', (e) =>
      this.skipScreenIndex(e.detail.skipIndex),
    );
  }

  /**
   * Sets the active screen by index and updates visibility
   * Applies CSS transform for smooth animation between screens
   * Updates accessibility attributes for hidden screens
   *
   * @param {number} screenIndex - The index of the screen to display (0-based)
   * @returns {void}
   * @throws {Error} If screenIndex is not a valid number
   *
   * @example
   * stepper.setScreen(0); // Show first screen
   * stepper.setScreen(2); // Show third screen
   */
  setScreen(screenIndex) {
    if (isNaN(Number(screenIndex)))
      throw new Error('[Stepper Error]: Screen index is missing!');

    this.currentIndex = screenIndex;

    const screens = this.screenContainer.children;

    this.stepper.setAttribute('data-active-screen', screenIndex);

    this.setTransform(screenIndex);

    this.updateActiveScreenVisibility();

    // Dispatch custom event for external listeners
    this.dispatchEvent(
      new CustomEvent('stepper:changed', {
        detail: { currentIndex: screenIndex, totalScreens: screens.length },
      }),
    );
  }

  /**
   * Navigates to the next screen
   * Does nothing if already on the last screen
   *
   * @returns {void}
   */
  nextScreen() {
    if (this.currentIndex < this.screenContainer.children.length - 1) {
      this.setScreen(this.currentIndex + 1);
    }
  }

  /**
   * Navigates to a specific screen by index
   * Validates that the screen index exists before navigation
   *
   * @param {number} screenIndex - The index of the screen to navigate to
   * @returns {void}
   * @throws {Error} If screenIndex is out of bounds
   */
  goToScreen(screenIndex) {
    if (screenIndex > this.children.length - 1)
      throw new Error(
        '[Stepper Error]: screenIndex does not exists in stepper screens!',
      );

    this.setScreen(screenIndex);
  }
  /**
   * Skip the screen according to the given value by incrementing the old value with the provided value
   * @param {*} skipIndex
   */
  skipScreenIndex(skipIndex) {
    if (!skipIndex) throw new Error('Skip index is missing!');

    const indexToSkip = Number(skipIndex);

    const newIndex = this.currentIndex + indexToSkip;
    this.currentIndex = newIndex;
    this.setScreen(newIndex);
  }

  /**
   * Navigates to the previous screen
   * Does nothing if already on the first screen
   *
   * @returns {void}
   */
  prevScreen() {
    if (this.currentIndex > 0) {
      this.setScreen(this.currentIndex - 1);
    }
  }

  /**
   * Updates visibility and accessibility of all screens
   * Sets inert attribute on hidden screens to prevent tab navigation and screen reader access
   * Updates data-active attribute to control CSS styling
   *
   * This ensures:
   * - Only the active screen is focusable
   * - Hidden screens are ignored by screen readers
   * - Proper keyboard navigation (Tab key skips hidden screens)
   *
   * @returns {void}
   * @private
   */
  updateActiveScreenVisibility() {
    const activeScreen = this.stepper?.dataset?.activeScreen;
    const screens = this.children;

    for (const screen of screens) {
      const screenIndex = screen.dataset?.screenIndex;

      if (screenIndex && screenIndex === activeScreen) {
        screen.inert = false;
        screen.removeAttribute('inert');
        screen.setAttribute('data-active', true);
      } else {
        screen.inert = true;
        screen.setAttribute('data-active', false);
        screen.setAttribute('inert', true);
      }
    }
  }

  /**
   * Applies CSS transform to animate the screen container
   * Moves the container horizontally to show the target screen
   * Uses CSS custom properties for smooth animation
   *
   * @param {number} screenIndex - The index of the screen to transform to
   * @returns {void}
   * @throws {Error} If screenIndex is not a valid number
   *
   * @private
   * @example
   * // Internally called by setScreen()
   * // For screen 0: --transform: 0%
   * // For screen 1: --transform: -100%
   * // For screen 2: --transform: -200%
   */
  setTransform(screenIndex) {
    if (isNaN(Number(screenIndex)))
      throw new Error('[Stepper Error]: screenIndex must be an integer!');

    this.screenContainer.style = `--transform:-${screenIndex * 100}%`;
  }

  /**
   * Handles breadcrumb click events
   * Navigates to the screen associated with the clicked breadcrumb
   *
   * @param {Event} e - The click event
   * @param {HTMLElement} item - The clicked breadcrumb element (must have data-index attribute)
   * @returns {void}
   *
   * @example
   * breadcrumb.addEventListener('click', (e) => {
   *   stepper.handleBreadcrumbClick(e, e.target);
   * });
   */
  handleBreadcrumbClick(e, item) {
    e.preventDefault();
    const screenIndex = Number(item.dataset.index);

    if (!isNaN(screenIndex)) {
      this.setScreen(Number(screenIndex));
    }
  }

  /**
   * Handles back/decrement button click events
   * Navigates to the previous screen
   *
   * @param {Event} e - The click event
   * @param {HTMLElement} btn - The clicked button element
   * @returns {void}
   *
   * @example
   * backButton.addEventListener('click', (e) => {
   *   stepper.handleDecrementClick(e, backButton);
   * });
   */
  handleDecrementClick(e, btn) {
    e.preventDefault();
    const activeScreen = Number(this.stepper.dataset.activeScreen);

    if (!isNaN(activeScreen)) {
      this.setScreen(activeScreen - 1);
    }
  }

  /**
   * Gets the current active screen index
   *
   * @returns {number} The index of the currently active screen (0-based)
   *
   * @example
   * const currentScreen = stepper.getCurrentScreen(); // 0, 1, 2, etc.
   */
  getCurrentScreen() {
    return this.currentIndex;
  }

  /**
   * Gets the total number of screens in the stepper
   *
   * @returns {number} The total count of screens
   *
   * @example
   * const total = stepper.getTotalScreens(); // 3
   */
  getTotalScreens() {
    return this.children.length;
  }
}

customElements.define('stepper-component', StepperComponent);
